import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import './react-datepicker.min.css';
import './DateInput.scss';
import CustomDateInput from './CustomDateInput';

interface DateInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  width?: string;
  'aria-label'?: string;
}

const DateInput: React.FC<DateInputProps> = ({
  id,
  value,
  onChange,
  placeholder = 'YY-MM-DD',
  disabled = false,
  className = '',
  width = 'auto',
  'aria-label': ariaLabel = 'Effective date'
}) => {
  // Helper function to parse YYYY-MM-DD string to local date at midnight
  const parseDateString = (dateString: string): Date | null => {
    if (!dateString) return null;

    // Parse YYYY-MM-DD format in local timezone
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;

    const year = Number.parseInt(parts[0], 10);
    const month = Number.parseInt(parts[1], 10);
    const day = Number.parseInt(parts[2], 10);

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;

    // Create date in local timezone at midnight
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };

  // State for selected date and validation error
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    return parseDateString(value);
  });
  const [error, setError] = useState<string | null>(null);
  
  // Update the selected date when the value prop changes
  useEffect(() => {
    setSelectedDate(parseDateString(value));
  }, [value]);

  // Format date as YYYY-MM-DD ensuring consistent format regardless of timezone
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    
    // Use local date components to avoid timezone offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  // Create a normalized today date to avoid time-related issues
  const getTodayDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Check if a date is today
  const isToday = (date: Date): boolean => {
    const today = getTodayDate();
    return date.getFullYear() === today.getFullYear() &&
           date.getMonth() === today.getMonth() &&
           date.getDate() === today.getDate();
  };

  // Default date to open the calendar to (always current month/year)
  const [calendarDefaultDate] = useState<Date>(getTodayDate());

  const handleChange = (date: Date | null) => {
    setSelectedDate(date);
    
    if (date) {
      // Create a clone of the selected date and normalize it to local midnight
      // This ensures consistent handling of the date without timezone interference
      const selectedDateNormalized = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        0, 0, 0, 0
      );
      
      // Get normalized today using the same method
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      
      // Check if the selected date is before today (using timestamp comparison)
      if (selectedDateNormalized.getTime() < today.getTime()) {
        setError('Please select today or a future date');
        return;
      }
      
      setError(null);
      // Use consistent formatting that preserves the selected date
      onChange(formatDateForInput(selectedDateNormalized));
    } else {
      setError(null);
      onChange('');
    }
  };

  // Helper function to highlight today's date
  const highlightToday = (date: Date): string | null => {
    return isToday(date) ? "react-datepicker__day--today" : null;
  };

  return (
    <div className={`date-input ${className}`} style={{ width }}>
      <DatePicker
        id={id}
        selected={selectedDate}
        onChange={handleChange}
        customInput={<CustomDateInput />}
        dateFormat="yy-MM-dd"
        placeholderText={placeholder}
        disabled={disabled}
        minDate={getTodayDate()} // Only allow today or future dates with normalized time
        className={`date-input__field ${disabled ? 'date-input__field--disabled' : ''} ${error ? 'date-input__field--error' : ''}`}
        aria-label={ariaLabel}
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        todayButton="Today"
        onSelect={(date) => handleChange(date)} // Ensure Today button works correctly
        isClearable={!disabled}
        shouldCloseOnSelect={true}
        dayClassName={highlightToday}
        openToDate={calendarDefaultDate} // Always open to current month/year regardless of selected date
        popperModifiers={[
          {
            name: 'preventOverflow',
            options: {
              rootBoundary: 'viewport',
              tether: false,
              altAxis: true,
            },
          },
        ]}
      />
      {error && <div className="date-input__error" aria-live="polite">{error}</div>}
    </div>
  );
};

export default DateInput;
